import React, { FC, useCallback, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogActions, Button, Checkbox, Input } from "@xapp/components";
import { AlertSubscription, AlertType, NotificationChannel, UserNotificationFilters } from "generated/graphql";
import { CONTACT_TYPES, URGENCY_LEVELS } from "../utils/constants";
import { SubscribableApp } from "../types";

export interface AlertSubscriptionEditorProps {
    subscription?: AlertSubscription | null;
    alertType: AlertType | null;
    subscribableApps: SubscribableApp[];
    active: boolean;
    onSave: (subscription: Partial<AlertSubscription>) => void;
    onDismiss: () => void;
}

export const AlertSubscriptionEditor: FC<AlertSubscriptionEditorProps> = (props) => {
    const { subscription, alertType, subscribableApps, active, onSave, onDismiss } = props;

    const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
    const [channel] = useState<NotificationChannel>(NotificationChannel.Email);
    const [contactTypes, setContactTypes] = useState<string[]>([]);
    const [highValueLead, setHighValueLead] = useState(false);
    const [jobTypes, setJobTypes] = useState("");
    const [urgencies, setUrgencies] = useState<string[]>([]);
    const [serviceTypes, setServiceTypes] = useState("");

    useEffect(() => {
        if (active && subscription) {
            setSelectedAppIds(subscription.appIds);
            setContactTypes(subscription.filters?.contactType || []);
            setHighValueLead(subscription.filters?.highValueLead || false);
            setJobTypes(subscription.filters?.jobType?.join(", ") || "");
            setUrgencies(subscription.filters?.urgency || []);
            setServiceTypes(subscription.filters?.serviceType?.join(", ") || "");
        } else if (active && !subscription) {
            setSelectedAppIds([]);
            setContactTypes([]);
            setHighValueLead(false);
            setJobTypes("");
            setUrgencies([]);
            setServiceTypes("");
        }
    }, [subscription, active]);

    const handleSave = useCallback(() => {
        const jobTypeArray = jobTypes
            .split(",")
            .map((j) => j.trim())
            .filter((j) => j.length > 0);

        const serviceTypeArray = serviceTypes
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

        const filters: Partial<UserNotificationFilters> = {};
        if (contactTypes.length > 0) filters.contactType = contactTypes;
        if (highValueLead) filters.highValueLead = highValueLead;
        if (jobTypeArray.length > 0) filters.jobType = jobTypeArray;
        if (urgencies.length > 0) filters.urgency = urgencies;
        if (serviceTypeArray.length > 0) filters.serviceType = serviceTypeArray;

        if (!alertType) {
            console.error("Alert type is required");
            return;
        }

        const savedSubscription: Partial<AlertSubscription> = {
            alertType,
            appIds: selectedAppIds,
            channel,
            filters: Object.keys(filters).length > 0 ? filters : undefined,
        };

        onSave(savedSubscription);
    }, [alertType, selectedAppIds, channel, contactTypes, highValueLead, jobTypes, urgencies, serviceTypes, onSave]);

    const isValid = selectedAppIds.length > 0;

    const getAlertTypeName = () => {
        switch (alertType) {
            case AlertType.LeadCaptured:
                return "Lead Captured";
            case AlertType.LeadAlert:
                return "Opportunity Alert";
            case AlertType.ChatStarted:
                return "Chat Started";
            default:
                return "Alert";
        }
    };

    return (
        <Dialog
            active={active}
            onDismiss={onDismiss}
            title={subscription ? `Edit ${getAlertTypeName()}` : `Add ${getAlertTypeName()}`}
        >
            <DialogContent>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div>
                        <label>Select Apps</label>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                            {subscribableApps.map((app) => (
                                <Checkbox
                                    key={app.appId}
                                    checked={selectedAppIds.includes(app.appId)}
                                    label={app.name}
                                    onChange={(checked) => {
                                        if (checked) {
                                            setSelectedAppIds([...selectedAppIds, app.appId]);
                                        } else {
                                            setSelectedAppIds(selectedAppIds.filter((id) => id !== app.appId));
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <p><strong>Channel:</strong> Email (SMS coming soon)</p>
                    </div>

                    <h4>Filters (optional)</h4>

                    <div>
                        <label>Contact Types</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                            {CONTACT_TYPES.map((type) => (
                                <Checkbox
                                    key={type.value}
                                    checked={contactTypes.includes(type.value)}
                                    label={type.label}
                                    onChange={(checked) => {
                                        if (checked) {
                                            setContactTypes([...contactTypes, type.value]);
                                        } else {
                                            setContactTypes(contactTypes.filter((t) => t !== type.value));
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <Checkbox
                        checked={highValueLead}
                        label="High Value Lead Only"
                        onChange={setHighValueLead}
                    />

                    <Input
                        label="Job Types"
                        helperText="Comma-separated job types (optional)"
                        value={jobTypes}
                        onChange={(value) => setJobTypes(value as string)}
                        variation="outlined"
                    />

                    <div>
                        <label>Urgency Levels</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                            {URGENCY_LEVELS.map((level) => (
                                <Checkbox
                                    key={level.value}
                                    checked={urgencies.includes(level.value)}
                                    label={level.label}
                                    onChange={(checked) => {
                                        if (checked) {
                                            setUrgencies([...urgencies, level.value]);
                                        } else {
                                            setUrgencies(urgencies.filter((u) => u !== level.value));
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <Input
                        label="Service Types"
                        helperText="Comma-separated service types (optional)"
                        value={serviceTypes}
                        onChange={(value) => setServiceTypes(value as string)}
                        variation="outlined"
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onDismiss}>Cancel</Button>
                <Button variation="primary" onClick={handleSave} disabled={!isValid}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
